import React, { useState, useRef } from 'react';
import { ICONS } from '../constants';
import { API_CONFIG } from '../config';

interface ShotThumbnailUploadProps {
  shotId: string;
  currentThumbnailUrl?: string;
  onThumbnailUpdate: (thumbnailUrl: string) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
}

const ShotThumbnailUpload: React.FC<ShotThumbnailUploadProps> = ({
  shotId,
  currentThumbnailUrl,
  onThumbnailUpdate,
  onContextMenu,
  onClick
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await uploadThumbnail(files[0]);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadThumbnail(files[0]);
    }
  };

  const uploadThumbnail = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('Only image files are allowed');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.error('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      const token = localStorage.getItem('spellbook_token');
      const formData = new FormData();
      formData.append('thumbnail', file);

      const response = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shotId}/thumbnail`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        onThumbnailUpdate(data.thumbnailUrl);
      } else {
        const errorText = await response.text();
        console.error('Failed to upload thumbnail:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className={`relative w-16 h-9 rounded cursor-context-menu transition-all ${
        dragActive ? 'ring-2 ring-indigo-500' : ''
      }`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onContextMenu={onContextMenu}
    >
      {isUploading ? (
        <div className="w-full h-full bg-gray-800 rounded flex items-center justify-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500" />
        </div>
      ) : currentThumbnailUrl ? (
        <div 
          className="relative w-full h-full group overflow-visible cursor-pointer"
          onClick={(e) => {
            if (onClick) {
              e.stopPropagation();
              onClick(e);
            }
          }}
        >
          <img
            src={currentThumbnailUrl.startsWith('http') ? currentThumbnailUrl : `${API_CONFIG.BASE_URL}${currentThumbnailUrl}`}
            alt="Shot thumbnail"
            className="w-full h-full object-cover rounded transition-all duration-200 group-hover:scale-150 group-hover:shadow-2xl group-hover:border-2 group-hover:border-indigo-500 relative"
            style={{ 
              transformOrigin: 'center center',
              zIndex: 1
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.zIndex = '9999';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.zIndex = '1';
            }}
          />
        </div>
      ) : (
        <div className="w-full h-full bg-gray-800 rounded flex items-center justify-center hover:bg-gray-700 transition-colors">
          <ICONS.Camera className="w-4 h-4 text-gray-600 group-hover:text-gray-400" />
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default ShotThumbnailUpload;

